import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TestTube, 
  Brain, 
  Apple, 
  Pill, 
  Activity, 
  Moon, 
  TrendingUp, 
  Target,
  ChevronRight,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

interface LoopStep {
  id: number;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  gradient: string;
}



const BiotuneLoopTimeline: React.FC = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [isVisible, setIsVisible] = useState(false);

  const handleComingSoon = (feature: string) => {
    toast.info(`🚀 ${feature} - Feature Coming Soon!`, {
      duration: 3000,
      position: 'top-center',
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => prev === 8 ? 1 : prev + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

// Fix hardcoded colors in the steps array
const biotuneSteps: LoopStep[] = [
  {
    id: 1,
    title: "Test",
    description: "Comprehensive biomarker analysis",
    icon: TestTube,
    color: "text-theme-primary",
    gradient: "from-blue-600 to-cyan-500"
  },
  {
    id: 2,
    title: "Analyze",
    description: "AI-powered data interpretation",
    icon: Brain,
    color: "text-theme-primary",
    gradient: "from-teal-600 to-cyan-500"
  },
  {
    id: 3,
    title: "Fuel",
    description: "Personalized nutrition planning",
    icon: Apple,
    color: "text-theme-primary",
    gradient: "from-green-600 to-emerald-500"
  },
  {
    id: 4,
    title: "Supplement",
    description: "Targeted nutrient optimization",
    icon: Pill,
    color: "text-theme-primary",
    gradient: "from-cyan-600 to-blue-500"
  },
  {
    id: 5,
    title: "Move",
    description: "Custom fitness protocols",
    icon: Activity,
    color: "text-theme-primary",
    gradient: "from-teal-600 to-green-500"
  },
  {
    id: 6,
    title: "Sleep",
    description: "Optimized recovery strategies",
    icon: Moon,
    color: "text-theme-primary",
    gradient: "from-blue-600 to-cyan-500"
  },
  {
    id: 7,
    title: "Track",
    description: "Continuous health monitoring",
    icon: TrendingUp,
    color: "text-theme-primary",
    gradient: "from-green-600 to-teal-500"
  },
  {
    id: 8,
    title: "Optimize",
    description: "Data-driven adjustments",
    icon: Target,
    color: "text-theme-primary",
    gradient: "from-emerald-600 to-teal-500"
  }
];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-theme-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-theme-primary mb-4">
            The BioTune Loop
          </h2>
          <p className="text-xl text-theme-secondary max-w-3xl mx-auto">
            Our proprietary 8-step process transforms your health data into personalized wellness insights
          </p>
        </motion.div>

        {/* Timeline */}
        <motion.div 
          className="relative"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {/* Connecting line */}
          <div className="absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 opacity-20 rounded-full" />
          
          {/* Steps */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 lg:gap-8">
            {biotuneSteps.map((step) => {
              const Icon = step.icon;
              const isActive = activeStep === step.id;
              
              return (
                <motion.div
                  key={step.id}
                  variants={stepVariants}
                  className="relative group cursor-pointer"
                  onMouseEnter={() => setActiveStep(step.id)}
                >
                  {/* Step number */}
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-theme-surface/70 rounded-full flex items-center justify-center z-10 shadow-lg border border-theme-border">
                    <span className="text-xs font-bold text-theme-text-inverse">
                      {step.id}
                    </span>
                  </div>

                  {/* Step circle */}
                  <motion.div
                    className={`relative w-32 h-32 mx-auto rounded-full bg-gradient-to-br ${step.gradient} p-1 shadow-xl border border-theme-border`}
                    whileHover={{ scale: 1.05 }}
                    animate={{
                      scale: isActive ? 1.1 : 1,
                      boxShadow: isActive 
                        ? `0 0 30px rgba(59, 130, 246, 0.4)` 
                        : "0 10px 25px rgba(0, 0, 0, 0.2)"
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                      '--hover-shadow': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                      '--hover-shadow-dark': '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    } as React.CSSProperties}
                    role="button"
                    aria-label={`Step ${step.id}: ${step.title}`}
                    tabIndex={0}
                  >
                    <div className="w-full h-full bg-theme-surface/70 rounded-full flex flex-col items-center justify-center p-4 border border-theme-border">
                      <motion.div
                        animate={{ 
                          rotate: isActive ? 360 : 0,
                          scale: isActive ? 1.1 : 1 
                        }}
                        transition={{ duration: 0.6 }}
                      >
                        <Icon className="w-8 h-8 text-theme-text-inverse drop-shadow-md mb-2" />
                      </motion.div>
                      <h3 className="text-sm font-bold text-theme-primary text-center">
                        {step.title}
                      </h3>
                    </div>
                  </motion.div>

                  {/* Description */}
                  <motion.div
                    className="mt-4 text-center"
                    animate={{ opacity: isActive ? 1 : 0.7 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-sm text-theme-muted leading-tight">
                      {step.description}
                    </p>
                  </motion.div>

                  {/* Active indicator */}
                  {isActive && (
                    <motion.div
                      className="absolute -bottom-8 left-1/2 transform -translate-x-1/2"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CheckCircle className="w-5 h-5 text-theme-success" />
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Call to action */}
        <motion.div 
          className="text-center mt-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          <p className="text-lg text-theme-secondary mb-6">
            Ready to start your personalized health journey?
          </p>
          <button 
            className="bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-theme-text-inverse font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center mx-auto space-x-2"
            onClick={() => handleComingSoon('BioTune Loop')}
          >
            <span>Begin Your BioTune Loop</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default BiotuneLoopTimeline;
